import { filter, get, map, omit } from 'lodash'
import { createSelector } from 'reselect'

import { RootState } from '@/store'
import { getAttributes } from '@/utilities/UserUtilities/selectors'

import { initialState } from './reducer'

const getState = (state: RootState) => get(state, 'organizationUserAttributes', initialState)
const getUserAttributesById = (state: RootState) => getState(state).byId

export const getAttributesInOrder = createSelector(getAttributes, attributes => {
  const attributesCopy = attributes.map(attr => ({ ...attr, _parent: null, _children: [] }))

  // get the name map, since there could be a couple with the same scim name add to array
  const nameMap = attributesCopy.reduce((names, attr) => {
    if (!names[attr.scimAttributeName]) names[attr.scimAttributeName] = []
    names[attr.scimAttributeName].push({ ...attr })
    return names
  }, {})

  // map the parents and children
  const parentChildAttributes = attributesCopy
    .map(attr => {
      const mapItem = nameMap[attr.scimParentAttributeName] || []
      const matchingParentAttribute = mapItem.filter((a: any) => attr.urn.startsWith(a.urn))
      const parent = matchingParentAttribute.length > 0 ? matchingParentAttribute[0] : null
      if (parent) parent._children.push(attr)
      attr._parent = parent

      return attr
    })
    .filter(a => a._parent === null)

  // get the root set of nodes that don't have any parents, sort in reverse order for stack operations
  parentChildAttributes.sort((a, b) => b.displayName.localeCompare(a.displayName))

  const stack = [...parentChildAttributes]

  const out = []
  while (stack.length > 0) {
    const attribute = stack.pop()
    out.push(omit(attribute, ['_children', '_parent']))
    if (attribute._children.length > 0) {
      attribute._children.sort((a: any, b: any) => b.displayName.localeCompare(a.displayName))
      stack.push(...attribute._children)
    }
  }

  return out
})

export const makeGetAttributeById = () => {
  return createSelector(
    [getUserAttributesById, (_, attributeId) => attributeId],
    (byId, attributeId) => byId[attributeId]
  )
}

export const makeGetDisplayNamesBySchema = () => {
  return createSelector([getUserAttributesById, (_, schema) => schema], (byId, schema) => {
    return map(schema ? filter(byId, { scimSchema: schema }) : byId, 'displayName')
  })
}

export const makeGetSCIMAttributeNamesBySchema = () => {
  return createSelector([getUserAttributesById, (_, schema) => schema], (byId, schema) => {
    return map(schema ? filter(byId, { scimSchema: schema }) : byId, 'scimAttributeName')
  })
}
